import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Package, TrendingUp, TrendingDown, AlertTriangle, 
  ArrowRightLeft, Warehouse, Users, Search, 
  ArrowUpDown, ArrowUp, ArrowDown, X, Filter,
  ChevronDown, ChevronUp, History, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Stock Level Card
const StockCard = ({ stock, onViewHistory }) => {
  const isCartonBased = stock.is_carton_based !== false;
  const isLowStock = stock.is_low_stock;
  
  return (
    <div 
      className={`bg-white rounded-lg border p-4 hover:shadow-md transition-shadow cursor-pointer ${isLowStock ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
      onClick={() => onViewHistory(stock)}
      data-testid={`stock-card-${stock.product_id}`}
    >
      <div className="flex justify-between items-start mb-2">
        <div>
          <span className="text-xs text-gray-500 uppercase">{stock.brand || 'Generic'}</span>
          <h4 className="font-semibold text-gray-800">{stock.product_name}</h4>
        </div>
        {isLowStock && (
          <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <AlertTriangle size={12} /> Low
          </span>
        )}
      </div>
      
      <div className="mt-3">
        {isCartonBased ? (
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">{stock.carton_qty || 0}</span>
            <span className="text-gray-500">cartons</span>
            {stock.loose_qty > 0 && (
              <>
                <span className="text-gray-400">+</span>
                <span className="text-lg font-semibold text-gray-700">{stock.loose_qty}</span>
                <span className="text-gray-500">loose</span>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">{stock.qty?.toFixed(1) || 0}</span>
            <span className="text-gray-500">{stock.unit_type || 'units'}</span>
          </div>
        )}
      </div>
      
      <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
        <div>
          {stock.godown_name && <span>📍 {stock.godown_name}</span>}
          {isCartonBased && stock.min_stock_cartons > 0 && (
            <span className="ml-2">Min: {stock.min_stock_cartons} cartons</span>
          )}
        </div>
        <History size={14} className="text-gray-400 hover:text-blue-500" />
      </div>
    </div>
  );
};

// Supplier Card
const SupplierCard = ({ supplier }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow" data-testid={`supplier-card-${supplier.id}`}>
    <h4 className="font-semibold text-gray-800">{supplier.name}</h4>
    {supplier.contact_name && (
      <p className="text-sm text-gray-500">{supplier.contact_name}</p>
    )}
    {supplier.phone && (
      <p className="text-sm text-gray-500">{supplier.phone}</p>
    )}
    <div className="mt-3 flex justify-between text-sm">
      <div>
        <span className="text-gray-500">Balance Due:</span>
        <span className={`ml-1 font-semibold ${supplier.credit_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
          ₹{(supplier.credit_balance || 0).toLocaleString()}
        </span>
      </div>
    </div>
  </div>
);

// Movement Row
const MovementRow = ({ movement }) => {
  const isIn = movement.movement_type === 'IN';
  const isCartonBased = movement.carton_qty !== null;
  
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm">
        {movement.created_at?.substring(0, 10)}
      </td>
      <td className="px-4 py-3">
        <div>
          <span className="font-medium">{movement.brand}</span>
          <span className="text-gray-600 ml-1">{movement.product_name}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
          ${isIn ? 'bg-green-100 text-green-700' : movement.movement_type === 'ADJUST' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
          {isIn ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {movement.movement_type}
        </span>
      </td>
      <td className="px-4 py-3 text-sm">
        {isCartonBased ? (
          <span>
            {Math.abs(movement.carton_qty || 0)} carton
            {movement.loose_qty ? ` + ${Math.abs(movement.loose_qty)} loose` : ''}
          </span>
        ) : (
          <span>{Math.abs(movement.qty || 0)} {movement.unit_type}</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {movement.reference_type}
      </td>
    </tr>
  );
};

// Stock History Modal
const StockHistoryModal = ({ stock, movements, onClose }) => {
  if (!stock) return null;
  
  const productMovements = movements.filter(m => m.product_id === stock.product_id);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="stock-history-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              {stock.brand} {stock.product_name}
            </h3>
            <p className="text-sm text-gray-500">Stock Movement History</p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            data-testid="close-history-modal"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4">
          {/* Current Stock Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="text-sm text-blue-600 font-medium mb-1">Current Stock</div>
            {stock.is_carton_based !== false ? (
              <div className="text-2xl font-bold text-blue-800">
                {stock.carton_qty || 0} cartons {stock.loose_qty > 0 ? `+ ${stock.loose_qty} loose` : ''}
              </div>
            ) : (
              <div className="text-2xl font-bold text-blue-800">
                {stock.qty?.toFixed(1) || 0} {stock.unit_type}
              </div>
            )}
          </div>
          
          {/* Movement History */}
          <div className="overflow-auto max-h-[400px]">
            {productMovements.length > 0 ? (
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {productMovements.map((m, idx) => (
                    <tr key={m.id || `pm-${idx}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm">{m.created_at?.substring(0, 10)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
                          ${m.movement_type === 'IN' ? 'bg-green-100 text-green-700' : 
                            m.movement_type === 'ADJUST' ? 'bg-yellow-100 text-yellow-700' : 
                            'bg-red-100 text-red-700'}`}>
                          {m.movement_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm">
                        {m.carton_qty !== null ? (
                          <span>{m.carton_qty} carton{m.loose_qty ? ` + ${m.loose_qty} loose` : ''}</span>
                        ) : (
                          <span>{m.qty} {m.unit_type}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">{m.reference_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No movement history for this product
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Inventory = ({ merchantId }) => {
  const [activeTab, setActiveTab] = useState('stock');
  const [stockLevels, setStockLevels] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('product_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterLowStock, setFilterLowStock] = useState(false);
  const [filterUnitType, setFilterUnitType] = useState('all');
  
  // Modal states
  const [selectedStock, setSelectedStock] = useState(null);
  
  useEffect(() => {
    if (merchantId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);
  
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [stockRes, lowStockRes, suppliersRes, movementsRes, productsRes, godownsRes] = await Promise.all([
        axios.get(`${API}/merchants/${merchantId}/stock`),
        axios.get(`${API}/merchants/${merchantId}/stock/low`),
        axios.get(`${API}/merchants/${merchantId}/suppliers`),
        axios.get(`${API}/merchants/${merchantId}/stock/movements?limit=100`),
        axios.get(`${API}/merchants/${merchantId}/products`),
        axios.get(`${API}/merchants/${merchantId}/godowns`),
      ]);
      
      setStockLevels(stockRes.data.stock_levels || []);
      setLowStockItems(lowStockRes.data.low_stock_items || []);
      setSuppliers(suppliersRes.data.suppliers || []);
      setMovements(movementsRes.data.movements || []);
      setProducts(productsRes.data.products || []);
      setGodowns(godownsRes.data.godowns || []);
    } catch (err) {
      console.error('Error fetching inventory data:', err);
      setError('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };
  
  // Get unique unit types for filter dropdown
  const unitTypes = useMemo(() => {
    const types = new Set(stockLevels.map(s => s.unit_type || 'pc'));
    return ['all', ...Array.from(types)];
  }, [stockLevels]);
  
  // Filter and sort stock levels
  const filteredStockLevels = useMemo(() => {
    let filtered = [...stockLevels];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        (s.product_name || '').toLowerCase().includes(query) ||
        (s.brand || '').toLowerCase().includes(query)
      );
    }
    
    // Low stock filter
    if (filterLowStock) {
      filtered = filtered.filter(s => s.is_low_stock);
    }
    
    // Unit type filter
    if (filterUnitType !== 'all') {
      filtered = filtered.filter(s => (s.unit_type || 'pc') === filterUnitType);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'product_name':
          aVal = (a.product_name || '').toLowerCase();
          bVal = (b.product_name || '').toLowerCase();
          break;
        case 'brand':
          aVal = (a.brand || '').toLowerCase();
          bVal = (b.brand || '').toLowerCase();
          break;
        case 'carton_qty':
          aVal = a.carton_qty || 0;
          bVal = b.carton_qty || 0;
          break;
        case 'qty':
          aVal = a.qty || a.carton_qty || 0;
          bVal = b.qty || b.carton_qty || 0;
          break;
        default:
          aVal = a[sortField] || '';
          bVal = b[sortField] || '';
      }
      
      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
    
    return filtered;
  }, [stockLevels, searchQuery, filterLowStock, filterUnitType, sortField, sortDirection]);
  
  // Filter suppliers by search
  const filteredSuppliers = useMemo(() => {
    if (!searchQuery.trim()) return suppliers;
    const query = searchQuery.toLowerCase();
    return suppliers.filter(s => 
      (s.name || '').toLowerCase().includes(query) ||
      (s.contact_name || '').toLowerCase().includes(query)
    );
  }, [suppliers, searchQuery]);
  
  // Filter movements by search
  const filteredMovements = useMemo(() => {
    if (!searchQuery.trim()) return movements;
    const query = searchQuery.toLowerCase();
    return movements.filter(m => 
      (m.product_name || '').toLowerCase().includes(query) ||
      (m.brand || '').toLowerCase().includes(query)
    );
  }, [movements, searchQuery]);
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const SortButton = ({ field, label }) => (
    <button 
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase hover:text-gray-700"
      data-testid={`sort-${field}`}
    >
      {label}
      {sortField === field ? (
        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
      ) : (
        <ArrowUpDown size={14} className="opacity-50" />
      )}
    </button>
  );
  
  if (!merchantId) {
    return (
      <div className="p-6 text-center text-gray-500" data-testid="no-merchant-selected">
        Please select a merchant to view inventory
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500" data-testid="inventory-loading">
        <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
        Loading inventory...
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6 text-center text-red-500" data-testid="inventory-error">
        {error}
        <button 
          onClick={fetchData}
          className="ml-2 text-blue-500 underline hover:text-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }
  
  const tabs = [
    { id: 'stock', label: 'Stock Levels', icon: Package, count: stockLevels.length },
    { id: 'movements', label: 'Movements', icon: ArrowRightLeft, count: movements.length },
    { id: 'suppliers', label: 'Suppliers', icon: Users, count: suppliers.length },
    { id: 'products', label: 'Products', icon: Package, count: products.length },
  ];
  
  return (
    <div className="p-6" data-testid="inventory-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Products</p>
              <p className="text-xl font-bold" data-testid="total-products">{products.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">In Stock</p>
              <p className="text-xl font-bold" data-testid="in-stock-count">{stockLevels.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="text-red-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Low Stock</p>
              <p className="text-xl font-bold text-red-600" data-testid="low-stock-count">{lowStockItems.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Warehouse className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Suppliers</p>
              <p className="text-xl font-bold" data-testid="supplier-count">{suppliers.length}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="low-stock-alert">
          <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
            <AlertTriangle size={18} />
            Low Stock Alert: {lowStockItems.length} items need restocking
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStockItems.slice(0, 5).map((item, idx) => (
              <span key={`${item.brand || 'nb'}-${item.product_name}-${idx}`} className="bg-white text-red-700 px-2 py-1 rounded text-sm border border-red-200">
                {item.brand} {item.product_name}
              </span>
            ))}
            {lowStockItems.length > 5 && (
              <span className="text-red-600 text-sm">+{lowStockItems.length - 5} more</span>
            )}
          </div>
        </div>
      )}
      
      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search products, brands, suppliers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="search-input"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
          
          {/* Low Stock Filter */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterLowStock}
              onChange={(e) => setFilterLowStock(e.target.checked)}
              className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
              data-testid="filter-low-stock"
            />
            <span className="text-sm text-gray-700 flex items-center gap-1">
              <AlertTriangle size={14} className="text-red-500" />
              Low Stock Only
            </span>
          </label>
          
          {/* Unit Type Filter */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={filterUnitType}
              onChange={(e) => setFilterUnitType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="filter-unit-type"
            >
              {unitTypes.map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All Units' : type}
                </option>
              ))}
            </select>
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={fetchData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            title="Refresh data"
            data-testid="refresh-btn"
          >
            <RefreshCw size={18} className="text-gray-500" />
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab.id 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} />
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'stock' && (
        <div>
          {/* Sort Options */}
          <div className="flex gap-4 mb-4 items-center">
            <span className="text-sm text-gray-500">Sort by:</span>
            <SortButton field="product_name" label="Name" />
            <SortButton field="brand" label="Brand" />
            <SortButton field="carton_qty" label="Quantity" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stock-grid">
            {filteredStockLevels.length > 0 ? (
              filteredStockLevels.map((stock, idx) => (
                <StockCard 
                  key={idx} 
                  stock={stock} 
                  onViewHistory={(s) => setSelectedStock(s)}
                />
              ))
            ) : (
              <div className="col-span-3 text-center text-gray-500 py-8" data-testid="no-stock-data">
                {searchQuery || filterLowStock || filterUnitType !== 'all' 
                  ? 'No items match your filters'
                  : 'No stock data available. Add products via voice or manually.'}
              </div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'movements' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="movements-table">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMovements.length > 0 ? (
                filteredMovements.map((movement, idx) => (
                  <MovementRow key={movement.id || `mv-${idx}`} movement={movement} />
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    {searchQuery ? 'No movements match your search' : 'No stock movements yet'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      
      {activeTab === 'suppliers' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="suppliers-grid">
          {filteredSuppliers.length > 0 ? (
            filteredSuppliers.map((supplier, idx) => (
              <SupplierCard key={supplier.id || `sp-${idx}`} supplier={supplier} />
            ))
          ) : (
            <div className="col-span-3 text-center text-gray-500 py-8">
              {searchQuery ? 'No suppliers match your search' : 'No suppliers yet. Add suppliers via voice or manually.'}
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'products' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="products-table">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pcs/Carton</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min Stock</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.length > 0 ? (
                products.map((product, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">{product.brand || '-'}</td>
                    <td className="px-4 py-3 text-sm">{product.name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="bg-gray-100 px-2 py-1 rounded text-xs">{product.unit_type}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {product.is_carton_based !== false ? product.pieces_per_carton : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {product.is_carton_based !== false 
                        ? `${product.min_stock_cartons || 0} cartons`
                        : `${product.min_stock_qty || 0} ${product.unit_type}`}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No products yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Stock History Modal */}
      {selectedStock && (
        <StockHistoryModal 
          stock={selectedStock}
          movements={movements}
          onClose={() => setSelectedStock(null)}
        />
      )}
    </div>
  );
};

export default Inventory;
